import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoCloseOutline } from 'react-icons/io5';
import FilterPanels from './EnhancedFilterModal/FilterPanels';
import { FilterItem } from '../types/notes';
import SearchBar from './EnhancedFilterModal/SearchBar';
import TagsDropdown from './EnhancedFilterModal/TagsDropdown';
import { extractTags } from '../utils/tagUtils';
import PersistentFilterModal from './EnhancedFilterModal/PersistentFilterModal';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reviewData: {
    reviewGoal: string;
    outputStyle: string;
    wordCount: number;
    selectedCards: FilterItem[];
    selectedNotes: FilterItem[];
  }) => void;
  initialNotes: FilterItem[];
  initialCards: FilterItem[];
  isSubmitting?: boolean;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialNotes,
  initialCards,
  isSubmitting = false,
}) => {
  const [reviewGoal, setReviewGoal] = useState('');
  const [outputStyle, setOutputStyle] = useState('bullet_point');
  const [wordCount, setWordCount] = useState('500');
  const [selectedNotes, setSelectedNotes] = useState<FilterItem[]>([]);
  const [selectedCards, setSelectedCards] = useState<FilterItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [typeVisibility, setTypeVisibility] = useState({
    notes: true,
    careerCards: true,
  });

  // Transform tags into the correct format using the utility
  const tags = useMemo(() => {
    const allItems = [...initialNotes, ...initialCards];
    const extractedTags = extractTags(allItems, {
      notes: {
        customTags: true,
        dateTags: true,
      },
      cards: {
        customTags: true,
        dateTags: true,
      },
    });
    return extractedTags;
  }, [initialNotes, initialCards]);

  // Filter items based on type visibility
  const filteredItems = useMemo(() => {
    const selectedNoteIds = new Set(selectedNotes.map(note => note.id));
    const selectedCardIds = new Set(selectedCards.map(card => card.id));

    return {
      notes: typeVisibility.notes 
        ? initialNotes.filter(note => !selectedNoteIds.has(note.id))
        : [],
      cards: typeVisibility.careerCards 
        ? initialCards.filter(card => !selectedCardIds.has(card.id))
        : [],
    };
  }, [initialNotes, initialCards, typeVisibility, selectedNotes, selectedCards]);

  const handleTypeVisibilityChange = (type: 'notes' | 'careerCards', value: boolean) => {
    setTypeVisibility(prev => ({
      ...prev,
      [type]: value,
    }));
  };

  // Use PersistentFilterModal's state management internally
  const handleFilterChange = (newNotes: FilterItem[], newCards: FilterItem[]) => {
    setSelectedNotes(newNotes);
    setSelectedCards(newCards);
  };

  const handleSubmit = () => {
    onSubmit({
      reviewGoal,
      outputStyle,
      wordCount: parseInt(wordCount),
      selectedCards,
      selectedNotes,
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      >
        <motion.div 
          className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-4xl max-h-[calc(100vh-2rem)] flex flex-col relative"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
        >
          <div className="flex justify-between items-center mb-4 shrink-0">
            <h2 className="text-xl font-bold">Create New Review</h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label="Close modal"
            >
              <IoCloseOutline size={24} />
            </button>
          </div>

          <div className="space-y-4 mb-4 shrink-0">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="reviewGoal">Review Goal</label>
              <input
                id="reviewGoal"
                type="text"
                value={reviewGoal}
                onChange={(e) => setReviewGoal(e.target.value)}
                className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600"
                placeholder="Enter your review goal..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="outputStyle">Output Style</label>
                <select
                  id="outputStyle"
                  value={outputStyle}
                  onChange={(e) => setOutputStyle(e.target.value)}
                  className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600"
                  title="Select output style"
                >
                  <option value="bullet_point">Bullet Point</option>
                  <option value="narrative">Narrative</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="wordCount">Word Count Target</label>
                <input
                  id="wordCount"
                  type="number"
                  value={wordCount}
                  onChange={(e) => setWordCount(e.target.value)}
                  className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600"
                  min="1"
                  step="1"
                  title="Enter target word count"
                />
              </div>
            </div>

            <div className="space-y-2">
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder="Search notes and cards..."
              />
              <TagsDropdown
                tags={tags}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                typeVisibility={typeVisibility}
                onTypeVisibilityChange={handleTypeVisibilityChange}
              />
            </div>
          </div>

          <div className="grow overflow-hidden">
            <FilterPanels
              availableItems={filteredItems}
              selectedItems={{
                notes: selectedNotes,
                cards: selectedCards,
              }}
              onItemSelect={(items) => {
                const newNotes = items.filter(item => item.type === 'note');
                const newCards = items.filter(item => item.type === 'card');
                handleFilterChange(
                  [...selectedNotes, ...newNotes],
                  [...selectedCards, ...newCards]
                );
              }}
              onItemDeselect={(items) => {
                const deselectedNoteIds = new Set(items.filter(item => item.type === 'note').map(item => item.id));
                const deselectedCardIds = new Set(items.filter(item => item.type === 'card').map(item => item.id));
                
                handleFilterChange(
                  selectedNotes.filter(note => !deselectedNoteIds.has(note.id)),
                  selectedCards.filter(card => !deselectedCardIds.has(card.id))
                );
              }}
              onMoveAllRight={() => {}}
              onMoveAllLeft={() => {}}
              searchQuery={searchQuery}
              selectedTags={selectedTags}
              getItemKey={(item: FilterItem) => `${item.type}-${item.id}`}
            />
          </div>

          <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-400"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !reviewGoal || selectedCards.length + selectedNotes.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Review'
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReviewModal; 